import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs-make";
import "./component.scss";

// Keep only in-tab navigation state; never persist personal completion here.
const scrollPositions = new Map();

export default class extends Controller {
  static targets = ["sortableList"];

  static values = {
    courseId: Number,
    canEdit: Boolean,
    reorderUrlTemplate: String,
  };

  connect() {
    if (scrollPositions.has(this.courseIdValue)) {
      this.sortableListTarget.scrollTop = scrollPositions.get(this.courseIdValue);
    }
    this.scrollToActiveItem();
    this.setupSortable();
  }

  disconnect() {
    this.destroySortable();
  }

  rememberScroll() {
    if (this.sortableListTarget.clientHeight > 0) {
      scrollPositions.set(this.courseIdValue, this.sortableListTarget.scrollTop);
    }
  }

  scrollToActiveItem() {
    const list = this.sortableListTarget;
    const activeItem = list.querySelector('[class*="--active"]');
    if (!activeItem || list.clientHeight === 0) return;

    const itemBounds = activeItem.getBoundingClientRect();
    const listBounds = list.getBoundingClientRect();
    if (itemBounds.bottom > listBounds.bottom) {
      list.scrollTop += itemBounds.bottom - listBounds.bottom;
    } else if (itemBounds.top < listBounds.top) {
      list.scrollTop += itemBounds.top - listBounds.top;
    }
  }

  setupSortable() {
    if (this.hasSortableListTarget && this.canEditValue) {
      this.sortable = Sortable.create(this.sortableListTarget, {
        animation: 150,
        handle: "[data-module-handle]",
        filter: "[data-sortable-exclude]",
        fallbackOnBody: true,
        forceFallback: true,
        swapThreshold: 0.85,
        onMove: (event) => {
          return !event.related.hasAttribute("data-sortable-exclude");
        },
        onEnd: this.onModuleReorder.bind(this),
      });

      this.lessonSortables = [];
      this.sortableListTarget
        .querySelectorAll("[data-lesson-reorder-url]")
        .forEach((ul) => {
          this.lessonSortables.push(
            Sortable.create(ul, {
              group: "lessons",
              animation: 150,
              draggable: "li",
              handle: "[data-lesson-handle]",
              fallbackOnBody: true,
              forceFallback: true,
              swapThreshold: 0.65,
              onEnd: (event) => this.onLessonReorder(event),
            }),
          );
        });
    }
  }

  destroySortable() {
    this.lessonSortables?.forEach((s) => s.destroy());
    this.lessonSortables = [];
    if (this.sortable) {
      this.sortable.destroy();
      this.sortable = null;
    }
  }

  onModuleReorder(event) {
    const id = event.item.dataset.courseModuleId;
    const newPosition = event.newIndex + 1;
    const url = this.reorderUrlTemplateValue.replace("__ID__", id);

    this.patchReorder(url, "course_module[new_position]", newPosition);
  }

  onLessonReorder(event) {
    const id = event.item.dataset.lessonId;
    const newPosition = event.newIndex + 1;
    const sourceList = event.from;
    const urlTemplate = sourceList.dataset.lessonReorderUrl;
    const url = urlTemplate.replace("__ID__", id);

    const data = new FormData();
    data.append("lesson[new_position]", newPosition);
    if (event.from !== event.to) {
      const newModuleId = event.to
        .closest("[data-course-module-id]")
        ?.dataset.courseModuleId;
      if (newModuleId) {
        data.append("lesson[course_module_id]", newModuleId);
      }
    }

    this.patchReorderWithData(url, data);
  }

  get csrfToken() {
    return document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");
  }

  patchReorder(url, paramName, newPosition) {
    const data = new FormData();
    data.append(paramName, newPosition);
    this.patchReorderWithData(url, data);
  }

  patchReorderWithData(url, data) {
    fetch(url, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": this.csrfToken,
      },
      body: data,
    })
      .then((response) => {
        if (!response.ok) throw new Error("Reorder failed");
      })
      .catch((error) => {
        console.error("Reorder error:", error);
        const frame = this.element.closest("turbo-frame");
        if (frame && frame.src) frame.src = frame.src;
      });
  }
}
